import React from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Grid
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

/**
 * SignatureDisplay Component
 * 
 * Displays existing signatures with options to edit or delete.
 * Shows a friendly message when no signature exists.
 * 
 * @param {Array} attachments - Array of signature attachments to display
 * @param {function} onEdit - Callback when edit signature button is clicked
 * @param {function} onDelete - Callback when delete signature button is clicked, receives attachment id
 * @param {boolean} disabled - Disables edit/delete functionality
 */
const SignatureDisplay = ({ attachments = [], onEdit, onDelete, disabled = false }) => {
    // Filter attachments to only show image files (signatures)
    const signatureAttachments = attachments.filter(att => 
        att.contentType && att.contentType.startsWith('image/')
    );

    // Show friendly message when no signatures exist
    if (signatureAttachments.length === 0) {
        return (
            <Box>
                <Paper 
                    elevation={1} 
                    sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        backgroundColor: '#f9f9f9',
                        border: '1px dashed #ccc'
                    }}
                >
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        No signature on file
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        Click "Add Signature" to capture a signature for this practitioner
                    </Typography>
                </Paper>
                
                <Box sx={{ mt: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={onEdit}
                        disabled={disabled}
                    >
                        Add Signature
                    </Button>
                </Box>
            </Box>
        );
    }

    // Display existing signatures with edit/delete options
    return (
        <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
                Current Signature:
            </Typography>
            
            <Grid container spacing={2}>
                {signatureAttachments.map((attachment) => (
                    <Grid item xs={12} key={attachment.id}>
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 2,
                                border: '1px solid #e0e0e0'
                            }}
                        >
                            {/* Display signature image */}
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                <img
                                    src={attachment.path}
                                    alt={attachment.fileName || 'Signature'}
                                    style={{ 
                                        maxWidth: '100%', 
                                        maxHeight: '150px',
                                        border: '1px solid #ccc',
                                        backgroundColor: '#fff'
                                    }}
                                />
                            </Box>
                            
                            {/* Action buttons */}
                            <Grid container spacing={1} justifyContent="center">
                                <Grid item>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={onEdit}
                                        disabled={disabled}
                                    >
                                        Replace Signature
                                    </Button>
                                </Grid>
                                <Grid item>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        startIcon={<DeleteIcon />}
                                        onClick={() => onDelete(attachment.id)}
                                        disabled={disabled}
                                    >
                                        Delete Signature
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default SignatureDisplay;